"use client";

import { useEffect, useRef } from "react";
import { gsap } from "@/lib/gsap";
import { getWindowDimensions, batchRead } from "@/lib/helper";

interface SuccessStoriesClientProps {
  children: React.ReactNode;
  cardsCount: number;
  background?: string;
}

export default function SuccessStoriesClient({
  children,
  cardsCount,
  background,
}: SuccessStoriesClientProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const caseStudiesContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!sectionRef.current || !caseStudiesContainerRef.current) return;

    const section = sectionRef.current;
    const container = caseStudiesContainerRef.current;

    const ctx = gsap.context(() => {
      const setupScrollTrigger = () => {
        const cardsContainer = container.querySelector(".flex.gap-8");
        if (!cardsContainer) return;

        // Batch all reads together to avoid forced reflow
        requestAnimationFrame(() => {
          const { maxScroll, viewportWidth, windowHeight } = batchRead(() => {
            return {
              maxScroll:
                cardsContainer.scrollWidth - cardsContainer.clientWidth,
              viewportWidth: getWindowDimensions().width,
              windowHeight: window.innerHeight,
            };
          });

          cardsContainer.scrollLeft = 0;

          if (maxScroll > 0) {
            let cardWidth = 500;
            if (viewportWidth < 640) {
              cardWidth = 280;
            } else if (viewportWidth < 768) {
              cardWidth = 350;
            } else if (viewportWidth < 1024) {
              cardWidth = 420;
            }

            const cardSpacing = 32;
            const isMobile = viewportWidth < 768;
            const scrollDistance = isMobile
              ? (cardWidth + cardSpacing) * cardsCount + windowHeight * 1.5
              : maxScroll * 1.5 + windowHeight;

            gsap.timeline({
              scrollTrigger: {
                trigger: section,
                start: "top top",
                end: `+=${scrollDistance}`,
                scrub: 1,
                pin: true,
                anticipatePin: 1,
                invalidateOnRefresh: true,
                id: "horizontalScroll",
                onUpdate: (self) => {
                  const progress = self.progress;
                  cardsContainer.scrollLeft = progress * maxScroll;
                },
              },
            });
          }
        });
      };

      // Delay to ensure DOM is ready
      const timer = setTimeout(setupScrollTrigger, 200);

      return () => clearTimeout(timer);
    }, section);

    return () => {
      ctx.revert();
    };
  }, [cardsCount]);

  return (
    <section
      ref={sectionRef}
      className={`relative ${background ? "bg-white" : "bg-primary-dark"} `}
    >
      <div className="sticky top-0 h-screen overflow-hidden flex items-center justify-center">
        <div className="w-full mx-auto">
          <div ref={caseStudiesContainerRef} className="w-full">
            {children}
          </div>
        </div>
      </div>
    </section>
  );
}
